// Запросы вкладки «Состав тела»: /api/inbody/*. Типы — зеркало схем
// backend/app/domain/schemas/inbody.py (те же имена полей). Ключи Query — ['inbody', ...];
// после записи сбрасывается всё ['inbody'], метрики (вес и жир из замера — общие точки) и «Сегодня».

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { api, apiUrl } from '@/api/client'
import { invalidateToday } from '@/api/queryClient'
import { metricKeys } from '@/pages/metrics/api'

export type Segment = 'left_arm' | 'right_arm' | 'trunk' | 'left_leg' | 'right_leg'
export type NormKey =
  | 'weight_kg'
  | 'skeletal_muscle_kg'
  | 'body_fat_pct'
  | 'total_body_water_l'
  | 'protein_kg'
  | 'minerals_kg'
  | 'visceral_fat_level'
  | 'bmi'
  | 'fat_free_mass_kg'
export type Status = 'below' | 'normal' | 'above'
export type WorkoutRelation = 'before' | 'after' | 'none'
export type ScanField =
  | 'score'
  | 'weight_kg'
  | 'skeletal_muscle_kg'
  | 'body_fat_pct'
  | 'body_fat_kg'
  | 'total_body_water_l'
  | 'protein_kg'
  | 'minerals_kg'
  | 'visceral_fat_level'
  | 'bmi'
  | 'fat_free_mass_kg'
  | 'bmr_kcal'
  | 'daily_kcal'
  | 'ideal_weight_kg'
  | 'to_ideal_weight_kg'
  | 'to_ideal_fat_kg'
  | 'to_ideal_muscle_kg'

export const SEGMENTS: readonly Segment[] = ['left_arm', 'right_arm', 'trunk', 'left_leg', 'right_leg']
export const NORM_KEYS: readonly NormKey[] = [
  'weight_kg',
  'skeletal_muscle_kg',
  'body_fat_pct',
  'total_body_water_l',
  'protein_kg',
  'minerals_kg',
  'visceral_fat_level',
  'bmi',
  'fat_free_mass_kg',
]

export type Norm = { low: number; high: number }
export type SegmentValue = { kg: number; pct_of_norm?: number | null }
export type Segments = { lean: Partial<Record<Segment, SegmentValue>>; fat: Partial<Record<Segment, SegmentValue>> }

export type ScanNumbers = { [K in ScanField]?: number | null }

export type ScanValues = ScanNumbers & {
  day: string
  weight_kg: number
  age?: number | null
  height_cm?: number | null
  sex?: 'M' | 'F' | null
  norms?: Partial<Record<NormKey, Norm>>
  segments?: Segments | null
}

export type HistoryPoint = {
  day: string
  weight_kg: number | null
  skeletal_muscle_kg: number | null
  body_fat_pct: number | null
}
export type ParseWarning = { field: string | null; message: string }
export type ParsedReport = { values: ScanValues; history: HistoryPoint[]; warnings: ParseWarning[] }

export type ScanIn = ScanValues & {
  measured_at?: string | null
  fasted?: boolean | null
  workout_relation?: WorkoutRelation | null
  note?: string | null
  draft_id?: string | null
  import_history?: boolean
}
export type ScanPatch = Partial<Omit<ScanIn, 'draft_id' | 'import_history'>>

export type AnalysisOut = {
  status: 'pending' | 'ok' | 'error' | 'not_configured'
  content_md: string | null
  error: string | null
  generated_at: string | null
}

export type ScanOut = ScanValues & {
  id: string
  complete: boolean
  measured_at: string
  fasted: boolean | null
  workout_relation: WorkoutRelation | null
  note: string | null
  has_pdf: boolean
  statuses: Partial<Record<NormKey, Status>>
  analysis: AnalysisOut | null
  created_at: string
  updated_at: string
}

export type DraftOut = {
  id: string
  filename: string | null
  uploaded_at: string
  origin: string
  parsed: ParsedReport
  existing_scan_id: string | null
  history_new_days: string[]
  saved_scan_id: string | null
  suggested_measured_at: string
}
export type DraftUploadOut = { draft: DraftOut; duplicate: boolean }

export type ComparisonRow = {
  field: ScanField
  label_ru: string
  unit: string | null
  last: number | null
  previous: number | null
  first: number | null
  delta_previous: number | null
  delta_first: number | null
  rate_previous_per_week: number | null
  rate_first_per_week: number | null
  better: 'up' | 'down' | 'none'
}
export type Recomposition = { muscle_delta_kg: number; fat_delta_kg: number; index_kg: number }
export type Asymmetry = {
  kind: 'lean' | 'fat'
  pair: 'arms' | 'legs'
  side: Segment
  diff_pct: number
  message_ru: string
}
export type Calibration = {
  metric: 'weight' | 'body_fat_pct'
  offset: number | null
  pairs: number
  message_ru: string
}
export type NutritionTargets = {
  protein_g: number
  protein_min_g: number
  protein_max_g: number
  maintenance_kcal: number | null
  target_kcal: number | null
  deficit: boolean
  avg_protein_g_7d: number | null
  avg_kcal_7d: number | null
}
export type InBodySummaryOut = {
  latest: ScanOut | null
  previous: ScanOut | null
  first: ScanOut | null
  comparison: ComparisonRow[]
  recomposition_previous: Recomposition | null
  recomposition_first: Recomposition | null
  conditions_differ: string | null
  asymmetry: Asymmetry[]
  nutrition: NutritionTargets | null
  calibration: Calibration[]
  days_since_last: number | null
  drafts: number
}
export type InBodyPoint = { day: string; value: number; complete: boolean }

export type ScansQuery = { start?: string | null; end?: string | null; complete_only?: boolean }
export type SeriesRange = { start?: string | null; end?: string | null }
export type UploadOrigin = 'upload' | 'share'

export const inbodyKeys = {
  all: ['inbody'] as const,
  summary: ['inbody', 'summary'] as const,
  scans: (q: ScansQuery = {}) => ['inbody', 'scans', q] as const,
  scan: (id: string) => ['inbody', 'scan', id] as const,
  drafts: ['inbody', 'drafts'] as const,
  draft: (id: string) => ['inbody', 'draft', id] as const,
  series: (field: ScanField, q: SeriesRange) => ['inbody', 'series', field, q] as const,
  calibration: ['inbody', 'calibration'] as const,
}

export const inbodyApi = {
  uploadDraft: (file: File, origin: UploadOrigin = 'upload') => {
    const form = new FormData()
    form.append('file', file)
    form.append('origin', origin)
    return api.post<DraftUploadOut>('/inbody/drafts', form)
  },
  drafts: () => api.get<DraftOut[]>('/inbody/drafts'),
  draft: (id: string) => api.get<DraftOut>(`/inbody/drafts/${id}`),
  discardDraft: (id: string) => api.delete(`/inbody/drafts/${id}`),
  scans: (q: ScansQuery = {}) => api.get<ScanOut[]>('/inbody/scans', { query: q }),
  scan: (id: string) => api.get<ScanOut>(`/inbody/scans/${id}`),
  saveScan: (data: ScanIn) => api.post<ScanOut>('/inbody/scans', data),
  updateScan: (id: string, patch: ScanPatch) => api.patch<ScanOut>(`/inbody/scans/${id}`, patch),
  deleteScan: (id: string) => api.delete<{ change_id: string }>(`/inbody/scans/${id}`),
  undo: (changeId: string) => api.post(`/changes/${changeId}/undo`),
  requestAnalysis: (id: string) => api.post<AnalysisOut>(`/inbody/scans/${id}/analysis`),
  suggestTime: (day: string, workoutRelation: WorkoutRelation | null) =>
    api.get<{ measured_at: string }>('/inbody/suggest-time', {
      query: { day, workout_relation: workoutRelation },
    }),
  summary: () => api.get<InBodySummaryOut>('/inbody/summary'),
  series: (field: ScanField, q: SeriesRange) =>
    api.get<InBodyPoint[]>('/inbody/series', { query: { field, start: q.start, end: q.end } }),
  calibration: () => api.get<Calibration[]>('/inbody/calibration'),
  pdfUrl: (id: string) => apiUrl(`/inbody/scans/${id}/pdf`),
  exportCsvUrl: () => apiUrl('/inbody/export.csv'),
}

export function useInBodySummary() {
  return useQuery({ queryKey: inbodyKeys.summary, queryFn: inbodyApi.summary })
}

export function useScans(q: ScansQuery = {}) {
  return useQuery({ queryKey: inbodyKeys.scans(q), queryFn: () => inbodyApi.scans(q) })
}

export function useScan(id: string | null | undefined) {
  return useQuery({
    queryKey: inbodyKeys.scan(id ?? ''),
    queryFn: () => inbodyApi.scan(id!),
    enabled: Boolean(id),
  })
}

export function useDrafts() {
  return useQuery({ queryKey: inbodyKeys.drafts, queryFn: inbodyApi.drafts })
}

export function useDraft(id: string | null | undefined) {
  return useQuery({
    queryKey: inbodyKeys.draft(id ?? ''),
    queryFn: () => inbodyApi.draft(id!),
    enabled: Boolean(id),
  })
}

export function useInBodySeries(field: ScanField, q: SeriesRange) {
  return useQuery({ queryKey: inbodyKeys.series(field, q), queryFn: () => inbodyApi.series(field, q) })
}

export function useCalibration() {
  return useQuery({ queryKey: inbodyKeys.calibration, queryFn: inbodyApi.calibration })
}

/** После записи замера: вкладка, общие метрики (вес и жир из замера) и «Сегодня». */
export function useInvalidateInBody() {
  const client = useQueryClient()
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: inbodyKeys.all }),
      client.invalidateQueries({ queryKey: metricKeys.all }),
      invalidateToday(client),
    ])
}
